import {
  MobileTypographyVariant,
  TypographyShade,
  TypographyVariant,
} from '../../components/typography/variants';
import {colorPrimitive} from '../../styles/primitives/colorPrimitive';

enum TypographyFontWeight {
  LIGHT = 300,
  REGULAR = 400,
  MEDIUM = 500,
  SEMIBOLD = 600,
  BOLD = 700,
}

interface TypographyDefinition {
  fontSize: string;
  lineHeight: string;
  fontWeight: TypographyFontWeight;
}

interface MobileTypographyDefinition {
  fontSize: number;
  lineHeight: number;
  fontWeight: TypographyFontWeight;
}

export type TypographySizeSemanticStyles = Record<TypographyVariant, TypographyDefinition>;
export type MobileTypographySizeSemanticsStyles = Record<MobileTypographyVariant, MobileTypographyDefinition>;
export type TypographyShadeSemanticStyles = Record<TypographyShade, string>;

export interface TypographySemanticStyles extends TypographySizeSemanticStyles {
  shades: TypographyShadeSemanticStyles;
}

export interface MobileTypographySemanticStyles extends MobileTypographySizeSemanticsStyles {
  shades: TypographyShadeSemanticStyles;
}

const typographySizeSemanticStyles = {
  /** Title */
  [TypographyVariant.TITLE_SEMIBOLD]: {
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [TypographyVariant.TITLE_BOLD]: {
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  /** Title Small */
  [TypographyVariant.TITLE_SMALL_SEMIBOLD]: {
    fontSize: '18px',
    lineHeight: '28px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [TypographyVariant.TITLE_SMALL_BOLD]: {
    fontSize: '18px',
    lineHeight: '28px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  /** Subtitle */

  [TypographyVariant.SUBTITLE_MEDIUM]: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [TypographyVariant.SUBTITLE_SEMIBOLD]: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [TypographyVariant.SUBTITLE_BOLD]: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  /** Subtitle Small */

  [TypographyVariant.SUBTITLE_SMALL_MEDIUM]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [TypographyVariant.SUBTITLE_SMALL_SEMIBOLD]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [TypographyVariant.SUBTITLE_SMALL_BOLD]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  [TypographyVariant.BODY_REGULAR]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [TypographyVariant.BODY_MEDIUM]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [TypographyVariant.BODY_SEMIBOLD]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [TypographyVariant.BODY_BOLD]: {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  [TypographyVariant.BODY_SMALL_REGULAR]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [TypographyVariant.BODY_SMALL_MEDIUM]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [TypographyVariant.BODY_SMALL_SEMIBOLD]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [TypographyVariant.BODY_SMALL_BOLD]: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  /** Caption */

  [TypographyVariant.CAPTION_REGULAR]: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [TypographyVariant.CAPTION_MEDIUM]: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [TypographyVariant.CAPTION_SEMIBOLD]: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },

  /** Caption Small */

  [TypographyVariant.CAPTION_SMALL_REGULAR]: {
    fontSize: '10px',
    lineHeight: '12px',
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [TypographyVariant.CAPTION_SMALL_SEMIBOLD]: {
    fontSize: '10px',
    lineHeight: '12px',
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },

  /** Display */

  [TypographyVariant.DISPLAY_BOLD]: {
    fontSize: '32px',
    lineHeight: '40px',
    fontWeight: TypographyFontWeight.BOLD,
  },

  /** Code */

  [TypographyVariant.CODE_REGULAR]: {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: TypographyFontWeight.REGULAR,
  },
} as const satisfies TypographySizeSemanticStyles;

const mobileTypographySizeSemanticStyles = {
  [MobileTypographyVariant.TITLE]: {
    fontSize: 24,
    lineHeight: 32,
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [MobileTypographyVariant.TITLE_BOLD]: {
    fontSize: 24,
    lineHeight: 32,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.TITLE_SMALL]: {
    fontSize: 18,
    lineHeight: 28,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.TITLE_SMALL_BOLD]: {
    fontSize: 18,
    lineHeight: 28,
    fontWeight: TypographyFontWeight.BOLD,
  },
  [MobileTypographyVariant.SUBTITLE]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [MobileTypographyVariant.SUBTITLE_BOLD]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [MobileTypographyVariant.SUBTITLE_BOLDER]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.SUBTITLE_BOLDEST]: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: TypographyFontWeight.BOLD,
  },
  [MobileTypographyVariant.SUBTITLE_SMALL]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [MobileTypographyVariant.SUBTITLE_SMALL_BOLD]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.SUBTITLE_SMALL_BOLDER]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.BOLD,
  },
  [MobileTypographyVariant.BODY]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [MobileTypographyVariant.BODY_BOLD]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [MobileTypographyVariant.BODY_BOLDER]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.BODY_BOLDEST]: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: TypographyFontWeight.BOLD,
  },
  [MobileTypographyVariant.BODY_SMALL]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [MobileTypographyVariant.BODY_SMALL_BOLD]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [MobileTypographyVariant.BODY_SMALL_BOLDER]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.BODY_SMALL_BOLDEST]: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: TypographyFontWeight.BOLD,
  },
  [MobileTypographyVariant.CAPTION]: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [MobileTypographyVariant.CAPTION_BOLD]: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: TypographyFontWeight.MEDIUM,
  },
  [MobileTypographyVariant.CAPTION_BOLDER]: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
  [MobileTypographyVariant.CAPTION_SMALL]: {
    fontSize: 10,
    lineHeight: 12,
    fontWeight: TypographyFontWeight.REGULAR,
  },
  [MobileTypographyVariant.CAPTION_SMALL_BOLD]: {
    fontSize: 10,
    lineHeight: 12,
    fontWeight: TypographyFontWeight.SEMIBOLD,
  },
} as const satisfies MobileTypographySizeSemanticsStyles;

const lightTypographySemanticShades = {
  primary: colorPrimitive.gray['1000'],
  secondary: colorPrimitive.gray['900'],
  tertiary: colorPrimitive.gray['700'],
  disabled: colorPrimitive.gray['400'],
  placeholder: colorPrimitive.gray['500'],

  inverse: colorPrimitive.gray.white,
  'inverse-secondary': colorPrimitive.gray['200'],

  'on-bg-fill': colorPrimitive.gray.white,
  'on-bg-fill-inverse': colorPrimitive.gray['1000'],
  'on-bg-fill-disabled': colorPrimitive.gray['400'],

  interactive: colorPrimitive.blue['700'],
  'interactive-hover': colorPrimitive.blue['800'],

  link: colorPrimitive.blue['600'],
  'link-hover': colorPrimitive.blue['700'],

  danger: colorPrimitive.red['600'],
  warning: colorPrimitive.orange['600'],
  success: colorPrimitive.green['600'],
  brand: colorPrimitive.purple['700'],
  code: colorPrimitive.red['700'],

  'text-selected': colorPrimitive.blurple['1000'],
  'text-unselected': colorPrimitive.gray['700'],
  'text-hover': colorPrimitive.gray['1000'],
} as const satisfies TypographyShadeSemanticStyles;

export const lightTypographySemanticStyles = {
  ...typographySizeSemanticStyles,
  shades: lightTypographySemanticShades,
} as const satisfies TypographySemanticStyles;

export const mobileLightTypographySemanticStyles = {
  ...mobileTypographySizeSemanticStyles,
  shades: lightTypographySemanticShades,
} as const satisfies MobileTypographySemanticStyles;

const darkTypographySemanticShades = {
  primary: colorPrimitive.gray['100'],
  secondary: colorPrimitive.gray['300'],
  tertiary: colorPrimitive.gray['400'],
  disabled: colorPrimitive.gray['700'],
  placeholder: colorPrimitive.gray['600'],

  inverse: colorPrimitive.gray.black,
  'inverse-secondary': colorPrimitive.gray['900'],

  'on-bg-fill': colorPrimitive.gray.white,
  'on-bg-fill-inverse': colorPrimitive.gray['1000'],
  'on-bg-fill-disabled': colorPrimitive.gray['600'],

  interactive: colorPrimitive.blue['500'],
  'interactive-hover': colorPrimitive.blue['600'],

  link: colorPrimitive.blue['500'],
  'link-hover': colorPrimitive.blue['600'],

  danger: colorPrimitive.red['500'],
  warning: colorPrimitive.yellow['500'],
  success: colorPrimitive.green['500'],
  brand: colorPrimitive.blue['500'],
  code: colorPrimitive.red['500'],

  'text-selected': colorPrimitive.blurple['100'],
  'text-unselected': colorPrimitive.gray['400'],
  'text-hover': colorPrimitive.blurple['100'],
} as const satisfies TypographyShadeSemanticStyles;

export const darkTypographySemanticStyles = {
  ...typographySizeSemanticStyles,
  shades: darkTypographySemanticShades,
} as const satisfies TypographySemanticStyles;

export const mobileDarkTypographySemanticStyles = {
  ...mobileTypographySizeSemanticStyles,
  shades: darkTypographySemanticShades,
} as const satisfies MobileTypographySemanticStyles;
